import { Drawer, Position } from '@blueprintjs/core';
import * as React from 'react';

import { ProjectDiagramAttributeDrawer } from 'src/containers/ProjectDiagram/components/attributeDrawer/ProjectDiagramAttributeDrawer';
import { ProjectDiagramClassDrawer } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawer';
import { ProjectDiagramEntityDrawerHeader } from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerHeader';
import { ProjectDiagramRelationshipDrawer } from 'src/containers/ProjectDiagram/components/relationshipDrawer/ProjectDiagramRelationshipDrawer';
import { ProjectDiagramResourceDrawer } from 'src/containers/ProjectDiagram/components/resourceDrawer/ProjectDiagramResourceDrawer';
import { ProjectDiagramSensitivePropertyGroupDrawer } from 'src/containers/ProjectDiagram/components/sensitivePropertyGroupDrawer/ProjectDiagramSensitivePropertyGroupDrawer';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const isToolbarElement = (el?: EventTarget): boolean => {
  if (
    el instanceof HTMLElement &&
    el.className.includes('sd-project-diagram-toolbar')
  ) {
    return true;
  }
  if (
    (el instanceof HTMLElement || el instanceof SVGElement) &&
    el.parentElement
  ) {
    return isToolbarElement(el.parentElement);
  }
  return false;
};

export const ProjectDiagramEntityDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const drawer = useReduxSelector(entityDrawerSelector);
  const isDrawerOpen = drawer.type !== EntityDrawerType.NONE;

  let body: JSX.Element | null = null;
  let title = '';

  if (drawer.type === EntityDrawerType.CLASS) {
    body = <ProjectDiagramClassDrawer />;
    title = copy.components.projectDiagram.classDrawer.title;
  } else if (drawer.type === EntityDrawerType.RELATIONSHIP) {
    body = <ProjectDiagramRelationshipDrawer />;
    title = copy.components.projectDiagram.relationshipDrawer.title;
  } else if (drawer.type === EntityDrawerType.RESOURCE) {
    body = <ProjectDiagramResourceDrawer />;
    title = copy.components.projectDiagram.resourceDrawer.title;
  } else if (drawer.type === EntityDrawerType.ATTRIBUTE) {
    body = <ProjectDiagramAttributeDrawer />;
    title = copy.components.projectDiagram.attributeDrawer.title;
  } else if (drawer.type === EntityDrawerType.SENSITIVE_PROPERTY_GROUP) {
    body = <ProjectDiagramSensitivePropertyGroupDrawer />;
    title = copy.components.projectDiagram.sensitivePropertyGroupDrawer.title;
  } else {
    // do nothing
  }

  return (
    <Drawer
      autoFocus={false}
      backdropClassName="sd-drawer-project-diagram-entity__backdrop"
      canEscapeKeyClose={false}
      className="sd-drawer-project-diagram-entity"
      enforceFocus={false}
      hasBackdrop
      isOpen={isDrawerOpen}
      lazy={false}
      onClose={(e) => {
        if (
          // we use goJS to deal with canvas interactions
          // to determine if drawer should be closed
          e?.target instanceof HTMLCanvasElement ||
          isToolbarElement(e?.target)
        ) {
          return;
        }

        dispatch(
          uiSlice.actions.changedEntityDrawer({
            projectId,
            drawer: {
              type: EntityDrawerType.NONE,
            },
          })
        );
      }}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={<ProjectDiagramEntityDrawerHeader title={title} />}
      usePortal={false}
    >
      {body}
    </Drawer>
  );
};
